import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import Pagination from '../../../components/common/Pagination';
import CreateLeadModal from './CreateLeadModal';
import ContentLoader from '../../../components/common/ContentLoader';
import SEO from '../../../components/common/SEO';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { formatDate, getLocalDateString } from '../../../utils/dateUtils';

const LeadListing = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const customerIdParam = searchParams.get('customer_id') || '';

    const [leads, setLeads] = useState([]);
    const [customerInfo, setCustomerInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);

    // Pagination states
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const [filters, setFilters] = useState({
        mobile: '',
        email: '',
        source: '',
        category: '',
        startDate: '',
        endDate: '',
        status: ''
    });

    const fetchLeads = React.useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                ...filters,
                page: page,
                limit: 10
            };
            if (customerIdParam) {
                params.customer_id = customerIdParam;
            }

            // Clean empty params
            Object.keys(params).forEach(key => {
                if (!params[key]) delete params[key];
            });

            const queryParams = new URLSearchParams(params);
            const response = await superAdminApiClient.get(`/superadmin/leads?${queryParams}`);
            setLeads(response.data.leads || []);
            setTotalPages(response.data.total_pages || 1);
            setTotalRecords(response.data.total_records || 0);
            setCustomerInfo(response.data.customer || null);
        } catch (error) {
            console.error('Error fetching leads', error);
        } finally {
            setLoading(false);
        }
    }, [filters, page, customerIdParam]);

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchLeads();
        }, 500);
        return () => clearTimeout(handler);
    }, [fetchLeads]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
        setPage(1);
    };

    const handleClearCustomerFilter = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('customer_id');
        setSearchParams(newParams);
        setCustomerInfo(null);
    };

    return (
        <React.Fragment>
            <SEO
                title="Lead Listing | Mangoloops"
                description="List of all leads on Mangoloops."
            />
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                        <h4 className="mb-sm-0">Lead Management</h4>
                        <div className="d-flex gap-2">
                            {customerIdParam && (
                                <Link to="/superadmin/registered-customers" className="btn btn-outline-secondary">
                                    <i className="ri-arrow-left-line align-bottom me-1"></i> Back to Registered Customers
                                </Link>
                            )}
                            <button className="btn btn-primary" onClick={() => setShowModal(true)}>Create Lead</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Customer Filter Alert Banner */}
            {customerIdParam && (
                <div className="alert alert-info d-flex align-items-center justify-content-between mb-3 shadow-sm rounded-3">
                    <div className="d-flex align-items-center">
                        <i className="ri-user-search-line fs-20 me-2 text-primary"></i>
                        <div>
                            <strong>Filtered Leads for Customer: </strong>
                            <span className="fw-bold text-dark me-2">
                                {customerInfo?.name || `ID #${customerIdParam}`}
                            </span>
                            {customerInfo?.phone && <span className="badge bg-light text-dark border me-1">📞 {customerInfo.phone}</span>}
                            {customerInfo?.email && <span className="badge bg-light text-dark border">✉️ {customerInfo.email}</span>}
                        </div>
                    </div>
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={handleClearCustomerFilter}
                    >
                        <i className="ri-close-line align-bottom me-1"></i> Clear Customer Filter
                    </button>
                </div>
            )}

            <div className="row row-cols-1 row-cols-md-5 mb-3 gy-2 gy-md-0">
                <div className="col">
                    <input type="text" className="form-control" placeholder="Lead Mobile" name="mobile" value={filters.mobile} onChange={handleFilterChange} />
                </div>
                <div className="col">
                    <input type="text" className="form-control" placeholder="Lead Email" name="email" value={filters.email} onChange={handleFilterChange} />
                </div>
                <div className="col">
                    <select className="form-select" name="source" value={filters.source} onChange={handleFilterChange}>
                        <option value="">All Sources</option>
                        <option value="Hot">Hot</option>
                        <option value="Manual">Manual</option>
                        <option value="Activity">Activity Lead</option>
                    </select>
                </div>
                <div className="col">
                    <select className="form-select" name="category" value={filters.category} onChange={handleFilterChange}>
                        <option value="">All Categories</option>
                        <option value="holidays">Holidays</option>
                        <option value="hotels">Hotels</option>
                        <option value="cab">Cab</option>
                        <option value="tirth_yatra">Tirth Yatra</option>
                        <option value="medical_tourism">Medical Tourism</option>
                    </select>
                </div>
                <div className="col">
                    <Flatpickr
                        className="form-control"
                        placeholder="Select Date Range"
                        options={{
                            mode: "range",
                            dateFormat: "Y-m-d"
                        }}
                        onChange={(dates) => {
                            if (dates.length === 2) {
                                const start = getLocalDateString(dates[0]);
                                const end = getLocalDateString(dates[1]);
                                setFilters({ ...filters, startDate: start, endDate: end });
                            } else if (dates.length === 0) {
                                setFilters({ ...filters, startDate: '', endDate: '' });
                            }
                            setPage(1);
                        }}
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {loading ? (
                                <ContentLoader />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Lead Name</th>
                                                <th>Mobile</th>
                                                <th>Category</th>
                                                <th>City</th>
                                                <th>Source</th>
                                                <th>Created At</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {leads.length > 0 ? leads.map((lead) => (
                                                <tr key={lead.id}>
                                                    <td>{lead.name} <br /><small className="text-muted">{lead.email}</small></td>
                                                    <td>{lead.mobile}</td>
                                                    <td><span className="badge bg-info">{lead.category}</span></td>
                                                    <td>{lead.city || 'N/A'}</td>
                                                    <td><span className="badges">{lead.source}</span></td>
                                                    <td>{formatDate(lead.created_at)}</td>
                                                    <td>
                                                        <Link to={`/superadmin/leads/detail/${lead.id}`} className="btn btn-sm btn-primary">
                                                            View Details
                                                        </Link>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="7" className="text-center">No leads found.</td></tr>
                                            )}
                                        </tbody>
                                    </table>

                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            Showing {leads.length} of {totalRecords} leads
                                        </div>
                                        <Pagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            onPageChange={(p) => setPage(p)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <CreateLeadModal show={showModal} handleClose={() => setShowModal(false)} refreshLeads={fetchLeads} />
        </React.Fragment>
    );
};

export default LeadListing;
